import React, { useState } from "react";
import { withTranslation } from "react-i18next";
import Modal from "@material-ui/core/Modal";
import {
  FaLeaf,
  FaStickyNote,
  FaCalendarAlt,
  FaPlus,
  FaPen,
  FaTrashAlt,
  FaTimes,
  FaCheck,
} from "react-icons/fa";
import ScheduledBadge, {
  getObsCardClass,
  ObservationExecutionInfo,
} from "./ScheduledBadge";
import { observationContainsMapPoint } from "../../helpers/observationDateUtils";
import "./Operation.css";

// Converts a date string to a datetime-local input value (YYYY-MM-DDTHH:MM) in the user's local timezone
function toDatetimeLocalValue(dateStr) {
  const d = new Date(dateStr);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Converts a datetime-local value to ISO string with the user's timezone offset
function toLocalISOString(dateStr) {
  const date = new Date(dateStr);
  const tzOffset = -date.getTimezoneOffset();
  const sign = tzOffset >= 0 ? "+" : "-";
  const hours = String(Math.floor(Math.abs(tzOffset) / 60)).padStart(2, "0");
  const minutes = String(Math.abs(tzOffset) % 60).padStart(2, "0");
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}${sign}${hours}:${minutes}`;
}

function ProductsModal({
  show,
  onHide,
  observations,
  onAddObservation,
  onUpdateObservation,
  onDeleteObservation,
  onConfirmObservation,
  prefillDate,
  filterDate,
  t,
}) {
  const [newStartDate, setNewStartDate] = useState("");
  const [newEndDate, setNewEndDate] = useState("");
  const [newProducts, setNewProducts] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editStartDate, setEditStartDate] = useState("");
  const [editEndDate, setEditEndDate] = useState("");
  const [editProducts, setEditProducts] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);

  // Filtrar observações por intervalo quando aberto pelo mapa
  const filteredObservations = React.useMemo(() => {
    if (!filterDate || !observations) return observations || [];
    return observations.filter((obs) =>
      observationContainsMapPoint(obs, filterDate)
    );
  }, [observations, filterDate]);

  const filterDateFormatted = React.useMemo(() => {
    if (!filterDate) return null;
    return new Date(filterDate).toLocaleDateString();
  }, [filterDate]);

  React.useEffect(() => {
    if (prefillDate) {
      setNewStartDate(toDatetimeLocalValue(prefillDate));
      setShowAddForm(true);
    }
  }, [prefillDate]);

  React.useEffect(() => {
    if (!show) {
      resetAddForm();
      setEditingId(null);
    }
  }, [show]);

  const resetAddForm = () => {
    setNewStartDate("");
    setNewEndDate("");
    setNewProducts("");
    setNewDescription("");
    setShowAddForm(false);
  };

  const handleAdd = () => {
    if (!newStartDate || !newEndDate || !newDescription.trim()) return;
    onAddObservation({
      start_date: toLocalISOString(newStartDate),
      end_date: toLocalISOString(newEndDate),
      products_used: newProducts,
      description: newDescription,
    });
    resetAddForm();
  };

  const startEditing = (obs) => {
    setEditingId(obs.id);
    setEditStartDate(toDatetimeLocalValue(obs.start_date));
    setEditEndDate(toDatetimeLocalValue(obs.end_date));
    setEditProducts(obs.products_used || "");
    setEditDescription(obs.description || "");
  };

  const cancelEditing = () => {
    setEditingId(null);
  };

  const handleUpdate = () => {
    if (!editStartDate || !editEndDate || !editDescription.trim()) return;
    onUpdateObservation(editingId, {
      start_date: toLocalISOString(editStartDate),
      end_date: toLocalISOString(editEndDate),
      products_used: editProducts,
      description: editDescription,
    });
    setEditingId(null);
  };

  return (
    <Modal
      aria-labelledby="products-modal-title"
      aria-describedby="products-modal-description"
      open={show}
      onClose={onHide}
    >
      <div
        className="modal-size"
        style={{
          maxWidth: "800px",
          padding: "20px",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <div className="x_title">
          <h2>
            <FaLeaf className="mr-2" style={{ marginRight: "8px" }} />
            {t("Produtos e Observações")}
            {filterDateFormatted && (
              <small
                style={{
                  fontWeight: "normal",
                  marginLeft: "10px",
                  fontSize: "14px",
                  color: "#666",
                }}
              >
                ({filterDateFormatted})
              </small>
            )}
          </h2>
          <div className="clearfix"></div>
        </div>

        <div className="x_content">
          {/* Lista de observações existentes */}
          {filteredObservations && filteredObservations.length > 0 ? (
            <div>
              {filteredObservations.map((obs) => (
                <div
                  key={obs.id}
                  className={getObsCardClass(obs)}
                  style={{
                    padding: "12px",
                    marginBottom: "10px",
                    ...(editingId === obs.id
                      ? { backgroundColor: "#f8f9fa" }
                      : {}),
                  }}
                >
                  {editingId === obs.id ? (
                    /* Modo edição */
                    <div>
                      <div className="row">
                        <div className="col-md-6 col-sm-12">
                          <div className="form-group">
                            <label>
                              <FaCalendarAlt style={{ marginRight: "5px" }} />
                              {t("Data/Hora Início")}
                            </label>
                            <input
                              type="datetime-local"
                              className="form-control"
                              value={editStartDate}
                              onChange={(e) => setEditStartDate(e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="col-md-6 col-sm-12">
                          <div className="form-group">
                            <label>
                              <FaCalendarAlt style={{ marginRight: "5px" }} />
                              {t("Data/Hora Fim")}
                            </label>
                            <input
                              type="datetime-local"
                              className="form-control"
                              value={editEndDate}
                              onChange={(e) => setEditEndDate(e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="form-group" style={{ marginTop: "8px" }}>
                        <label>
                          <FaLeaf style={{ marginRight: "5px" }} />
                          {t("Produtos Utilizados")}
                        </label>
                        <textarea
                          className="form-control"
                          rows="3"
                          value={editProducts}
                          onChange={(e) => setEditProducts(e.target.value)}
                          placeholder={t(
                            "Ex: Herbicida XYZ - 2L/ha (100L total)",
                          )}
                        />
                      </div>
                      <div className="form-group" style={{ marginTop: "8px" }}>
                        <label>
                          <FaStickyNote style={{ marginRight: "5px" }} />
                          {t("Descrição")}
                        </label>
                        <textarea
                          className="form-control"
                          rows="3"
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                        />
                      </div>
                      <div style={{ marginTop: "10px", textAlign: "right" }}>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={cancelEditing}
                          style={{ marginRight: "5px" }}
                        >
                          <FaTimes style={{ marginRight: "4px" }} />
                          {t("Cancelar")}
                        </button>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={handleUpdate}
                        >
                          <FaCheck style={{ marginRight: "4px" }} />
                          {t("Salvar")}
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Modo visualização */
                    <div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <ObservationExecutionInfo obs={obs} t={t} />
                          <ScheduledBadge
                            obs={obs}
                            onConfirm={onConfirmObservation}
                          />
                          {obs.products_used && (
                            <div style={{ marginTop: "6px" }}>
                              <small>
                                <strong>
                                  <FaLeaf style={{ marginRight: "4px" }} />
                                  {t("Produtos Utilizados")}:
                                </strong>
                              </small>
                              <pre
                                style={{
                                  whiteSpace: "pre-wrap",
                                  fontSize: "12px",
                                  margin: "2px 0",
                                  padding: "4px 8px",
                                  backgroundColor: "#f5f5f5",
                                  borderRadius: "4px",
                                }}
                              >
                                {obs.products_used}
                              </pre>
                            </div>
                          )}
                          <div style={{ marginTop: "4px" }}>
                            <small>
                              <strong>
                                <FaStickyNote style={{ marginRight: "4px" }} />
                                {t("Descrição")}:
                              </strong>
                            </small>
                            <p
                              style={{
                                fontSize: "13px",
                                margin: "2px 0",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {obs.description}
                            </p>
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: "5px" }}>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => startEditing(obs)}
                            title={t("Editar")}
                          >
                            <FaPen />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => onDeleteObservation(obs.id)}
                            title={t("Excluir")}
                          >
                            <FaTrashAlt />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div
              className="alert alert-info"
              style={{ textAlign: "center", marginBottom: "15px" }}
            >
              {t("Nenhuma observação registrada")}
            </div>
          )}

          {/* Formulário de adicionar observação */}
          {showAddForm ? (
            <div
              style={{
                border: "2px dashed #28a745",
                borderRadius: "8px",
                padding: "15px",
                marginTop: "15px",
                backgroundColor: "#f8fff8",
              }}
            >
              <h5>
                <FaPlus style={{ marginRight: "5px" }} />
                {t("Adicionar Observação")}
              </h5>
              <div className="row">
                <div className="col-md-6 col-sm-12">
                  <div className="form-group">
                    <label>
                      <FaCalendarAlt style={{ marginRight: "5px" }} />
                      {t("Data/Hora Início")} *
                    </label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={newStartDate}
                      onChange={(e) => setNewStartDate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-6 col-sm-12">
                  <div className="form-group">
                    <label>
                      <FaCalendarAlt style={{ marginRight: "5px" }} />
                      {t("Data/Hora Fim")} *
                    </label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={newEndDate}
                      onChange={(e) => setNewEndDate(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="form-group" style={{ marginTop: "10px" }}>
                <label>
                  <FaLeaf style={{ marginRight: "5px" }} />
                  {t("Produtos Utilizados")}
                </label>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder={t(
                    "Ex: Herbicida XYZ - 2L/ha (100L total)\nAdjuvante ABC - 0.5L/ha (25L total)\nFertilizante DEF - 200kg/ha",
                  )}
                  value={newProducts}
                  onChange={(e) => setNewProducts(e.target.value)}
                />
                <small className="form-text text-muted">
                  {t("Liste os produtos, doses e quantidades utilizadas")}
                </small>
              </div>
              <div className="form-group" style={{ marginTop: "10px" }}>
                <label>
                  <FaStickyNote style={{ marginRight: "5px" }} />
                  {t("Descrição")} *
                </label>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder={t(
                    "Condições climáticas, ocorrências, horários, etc.",
                  )}
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                />
              </div>
              <div style={{ marginTop: "12px", textAlign: "right" }}>
                <button
                  className="btn btn-secondary"
                  onClick={resetAddForm}
                  style={{ marginRight: "8px" }}
                >
                  {t("Cancelar")}
                </button>
                <button
                  className="btn btn-success"
                  onClick={handleAdd}
                  disabled={
                    !newStartDate || !newEndDate || !newDescription.trim()
                  }
                >
                  <FaPlus style={{ marginRight: "5px" }} />
                  {t("Adicionar Observação")}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: "center", marginTop: "15px" }}>
              <button
                className="btn btn-success"
                onClick={() => setShowAddForm(true)}
              >
                <FaPlus style={{ marginRight: "5px" }} />
                {t("Adicionar Observação")}
              </button>
            </div>
          )}

          {/* Botão fechar */}
          <div style={{ marginTop: "20px", textAlign: "center" }}>
            <button className="btn btn-secondary" onClick={onHide}>
              {t("Fechar")}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default withTranslation()(ProductsModal);
